import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, DollarSign, TrendingUp, BarChart2, Users, Calendar } from 'lucide-react';
import './MarketplacePage.css';

function MarketplacePage({ hasSubscribed }) {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchListings = useCallback(async () => {
    try {
      setIsLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await axios.get(`${API_URL}/api/listings`, {
        params: { _t: Date.now() } // This prevents caching
      });
      setListings(response.data);
      setFilteredListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  useEffect(() => {
    if (location.pathname === '/marketplace') {
      fetchListings();
    }
  }, [location, fetchListings]);

  useEffect(() => {
    const filtered = listings.filter(listing =>
      (statusFilter === 'All' || listing.status === statusFilter) &&
      listing.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredListings(filtered);
  }, [statusFilter, searchTerm, listings]);

  const handleViewDetails = (id) => {
    if (hasSubscribed) {
      navigate(`/listing/${id}`);
    } else {
      alert('Please subscribe to view listing details.');
      navigate('/pricing');
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  if (isLoading) {
    return <div className="loading"></div>;
  }

  return (
    <div className="marketplace-container">
      <h1>Explore Marketplace</h1>
      <div className="filters">
        <div className="search-bar">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search listings..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <div className="status-filter">
          <Filter className="filter-icon" />
          <select value={statusFilter} onChange={handleStatusFilterChange}>
            <option value="All">All Status &nbsp;</option>
            <option value="Available">Available</option>
            <option value="Pending">Pending</option>
            <option value="Sold">Sold</option>
          </select>
        </div>
      </div>
      <div className="marketplace-grid">
        {filteredListings.map((listing) => (
          <div key={listing._id} className="marketplace-card">
            <h3>{listing.title}</h3>
            <p className="description">{listing.description}</p>
            <div className="price">
              <DollarSign className="icon" />
              {listing.price} USDT
            </div>
            <div className="metrics">
              <div className="metric">
                <TrendingUp className="icon" />
                <span>TTM Revenue: {listing.ttmRevenue} USDT</span>
              </div>
              <div className="metric">
                <BarChart2 className="icon" />
                <span>TTM Profit: {listing.ttmProfit} USDT</span>
              </div>
              <div className="metric">
                <Calendar className="icon" />
                <span>Last Month Revenue: {listing.lastMonthRevenue} USDT</span>
              </div>
              <div className="metric">
                <Calendar className="icon" />
                <span>Last Month Profit: {listing.lastMonthProfit} USDT</span>
              </div>
              <div className="metric">
                <Users className="icon" />
                <span>Team Size: {listing.teamSize}</span>
              </div>
            </div>
            <div className={`status ${listing.status.toLowerCase()}`}>
              {listing.status}
            </div>
            <button
              className="view-details-button"
              onClick={() => handleViewDetails(listing._id)}
            >
              {hasSubscribed ? 'View Details' : 'Subscribe to View'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MarketplacePage;